import {
  type AllBoundingBoxControls,
  type BoundingBoxClassControl,
} from '@wandb/weave/common/components/MediaCard';
import {DEFAULT_ALL_MASK_CONTROL} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import Color from 'color';
import {get} from 'lodash';
import React, {type JSX, useCallback, useMemo} from 'react';

import {segmentationMaskColor} from '../../../../../../util/media';
import {useMediaBoundingBoxConfigDisplay} from '../../../hooks';
import {OverlaysTable} from '../../common/OverlaysTable';
import {OverlaysTableRow} from '../../common/OverlaysTableRow';
import {applyOverlayToggleUpdate} from '../../common/overlayToggleUpdate';
import {useMediaSettingsContext} from '../../contexts/MediaSettingsContextProvider';
import {BoundingBoxStrokeTypeSelector} from './BoundingBoxStrokeTypeSelector';

// A bounding box class retains its entry (minus `disabled`) only when it
// carries a custom color; otherwise the entry is dropped entirely.
const boxEntryHasCustomization = (entry: BoundingBoxClassControl): boolean =>
  entry.color != null;

/**
 * Pure function: apply a bounding box toggle update and return the new config.
 * When updating the box "all" control, removes child `disabled` overrides so
 * classes inherit visibility from the parent while preserving colors.
 */
export function applyBoundingBoxToggleUpdate(
  config: AllBoundingBoxControls | undefined,
  configPath: Array<string>,
  currentControl: BoundingBoxClassControl | undefined,
  newControl: Partial<BoundingBoxClassControl>
): AllBoundingBoxControls {
  return applyOverlayToggleUpdate(
    config,
    configPath,
    currentControl,
    newControl,
    boxEntryHasCustomization
  );
}

export const BoundingBoxClasses = ({
  boxKey,
  mediaKey,
  boxQueryRegex,
}: {
  boxKey: string;
  mediaKey: string;
  boxQueryRegex: RegExp;
}) => {
  const {allBoxes, updateConfig, settingsLevel, panelRef, mediaCardTypes} =
    useMediaSettingsContext();
  const displayedBoundingBoxConfig = useMediaBoundingBoxConfigDisplay(
    settingsLevel,
    panelRef
  );
  const boundingBoxConfig = useMemo(
    () => displayedBoundingBoxConfig ?? {},
    [displayedBoundingBoxConfig]
  );
  const classDict = allBoxes[mediaKey][boxKey].value;
  const allConfigPath = ['toggles', mediaKey, boxKey, 'all'];
  const allBoxControl: BoundingBoxClassControl =
    get(boundingBoxConfig, allConfigPath) ?? DEFAULT_ALL_MASK_CONTROL;

  const update = useCallback(
    (
      newControl: Partial<BoundingBoxClassControl>,
      configPath: Array<string>
    ) => {
      const boxControl = get(boundingBoxConfig, configPath);
      const newConfig = applyBoundingBoxToggleUpdate(
        boundingBoxConfig,
        configPath,
        boxControl,
        newControl
      );
      updateConfig({boundingBoxConfig: newConfig});
    },
    [boundingBoxConfig, updateConfig]
  );
  // This defines the hierarchy of bounding box controls. The child bounding box controls can override
  // the parent bounding box controls. This is so that the user can hide all bounding boxes, but then enable
  // a specific bounding box.
  const isAllBoundingBoxesDisabled = allBoxControl.disabled;
  const getIsBoundingBoxDisabled = (
    boxControl: BoundingBoxClassControl | undefined
  ) => boxControl?.disabled ?? isAllBoundingBoxesDisabled;

  return (
    <OverlaysTable
      isAllVisible={!allBoxControl.disabled}
      setIsAllVisible={() =>
        update({disabled: !allBoxControl.disabled}, allConfigPath)
      }
      title={boxKey}
      rightContent={
        // Only image media panels support stroke type selection for bounding boxes
        mediaCardTypes?.some(mediaCardType => mediaCardType === 'image') ? (
          <BoundingBoxStrokeTypeSelector
            mediaKey={mediaKey}
            boxKey={boxKey}
            boxConfig={boundingBoxConfig}
            updateConfig={updateConfig}
          />
        ) : undefined
      }>
      {Object.entries(classDict).reduce<JSX.Element[]>((acc, [k, name]) => {
        if (!name.match(boxQueryRegex)) {
          return acc; // Skip if it doesn't match
        }
        const configPath = ['toggles', mediaKey, boxKey, k];

        const boxControl: BoundingBoxClassControl = get(
          boundingBoxConfig,
          configPath
        );

        const onClickHandler = () =>
          update({disabled: !getIsBoundingBoxDisabled(boxControl)}, configPath);
        const setColor = (newColor: string) => {
          const c = Color(newColor);
          update({color: [c.red(), c.green(), c.blue()]}, configPath);
        };

        acc.push(
          <OverlaysTableRow
            data-test="bounding-box-class-row"
            key={mediaKey + boxKey + name}
            title={name}
            isVisible={!getIsBoundingBoxDisabled(boxControl)}
            setIsVisible={onClickHandler}
            setColor={
              mediaCardTypes?.some(mediaCardType => mediaCardType === 'image')
                ? setColor
                : undefined
            }
            color={
              // Bounding boxes can be customized for point clouds via SDK so they might be all
              // different colors even if they have the same label. You can't
              // edit it either via UI, so just hide it.
              mediaCardTypes?.some(mediaCardType => mediaCardType === 'image')
                ? (boxControl?.color ?? segmentationMaskColor(Number(k)))
                : undefined
            }
          />
        );

        return acc;
      }, [])}
    </OverlaysTable>
  );
};
